import { Button, Form, ModalManagerContext, Text, ColumnProps, Card, Banner } from '@pega/cosmos-react-core';
import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';

import useLocalTranslation from '../../../hooks/useLocalTranslation';
import { StyledTable } from '../../Utilities/custom.styled';
import { MaxNumberOfCustomers } from '../../../constants/common';
import { updateBannerInfo, updateLoaderDetails } from '../../../redux/actions';
import { useDispatch, useSelector } from 'react-redux';
import { appState } from '../../../interfaces/common';
import { genAiUpdatePersona } from './AISimulatorApis';

interface BulkUploadPersonaProps {
  excelData: any;
  customerData: any;
  connectionId: string;
  setAccountDetails: Dispatch<SetStateAction<any>>;
}

const BulkUploadPersona: React.FC<BulkUploadPersonaProps> = ({
  excelData,
  customerData,
  setAccountDetails,
  connectionId,
}) => {
  const sessionId = useSelector((state: appState) => state.sessionId);
  const dispatch = useDispatch();

  const translate = useLocalTranslation();
  const { ModalContext } = useContext(ModalManagerContext);
  const { dismiss } = useContext(ModalContext);
  const [disableUpload, setDisableUpload] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [customerPayload, setCustomerPayload] = useState([]);

  const validatePayload = (payload: any) => {
    return !!(
      payload['Customer Id'] &&
      payload['Customer Name'] &&
      payload['Email'] &&
      payload['Phone Number'] &&
      payload['Full Address'] &&
      payload['Date of Birth'] &&
      payload['Account Details'] &&
      payload['Occupation']
    );
  };

  useEffect(() => {
    const newDataLen = excelData.length;
    const totalLen = customerData.length + newDataLen;
    if (totalLen > MaxNumberOfCustomers) {
      setDisableUpload(true);
      setErrorMessage('length_exceeded');
    } else if (!newDataLen) {
      setDisableUpload(true);
      setErrorMessage('excel_empty');
    }
  }, [customerData, excelData]);

  useEffect(() => {
    const tempRecords: any = [];
    try {
      excelData?.forEach((item: { [x: string]: any }) => {
        const isValid = validatePayload(item);
        if (!isValid) {
          throw new Error('missing_complete_payload');
        }

        const findIndex = tempRecords?.findIndex(function (data: any) {
          return data.customerId === item['Customer Id'];
        });
        if (tempRecords.length > 0 && findIndex !== -1) {
          throw new Error('custId_unique');
        }
        const customer = {
          bio: {
            full_name: item['Customer Name'],
            dob: item['Date of Birth'],
            address: {
              full_address: item['Full Address'],
            },
            email_address: item['Email'],
            phone_number: item['Phone Number'],
            occupation: item['Occupation'],
          },
          accountDetails: item['Account Details'],
          customerId: item['Customer Id'],
        };
        tempRecords.push(customer);
      });
    } catch (error: any) {
      setDisableUpload(true);
      setErrorMessage(error.message);
    }
    setCustomerPayload(tempRecords);
  }, [excelData]);

  const uploadExcelData = async () => {
    const payload = {
      newCustomerData: customerPayload,
    };
    dispatch(updateLoaderDetails(true));
    const response = await genAiUpdatePersona(sessionId, connectionId, payload, 'bulk_upload_personas');
    if (response?.status === 200) {
      setAccountDetails(response?.data);
      dispatch(updateBannerInfo(translate('updated_customer_details'), 'success'));
      dispatch(updateLoaderDetails(false));
      dismiss();
    } else {
      dispatch(
        updateBannerInfo(
          response?.errorMessage ? translate(response?.errorMessage) : translate('customer_details_failed'),
          'urgent',
        ),
      );
      dispatch(updateLoaderDetails(false));
    }
  };

  const actions = (
    <>
      <Button onClick={dismiss}>{translate('cancel')}</Button>
      <Button variant="primary" onClick={uploadExcelData} disabled={disableUpload}>
        {translate('submit')}
      </Button>
    </>
  );

  const data = excelData?.map((item: any, index: any) => {
    return {
      id: index,
      name: <Text>{item['Customer Name']}</Text>,
      customerId: <Text>{item['Customer Id']}</Text>,
      email: <Text>{item['Email']}</Text>,
    };
  });

  const columns: ColumnProps<typeof data[number]>[] = [
    { renderer: 'name', label: translate('name') },
    { renderer: 'customerId', label: translate('customer_id') },
    { renderer: 'email', label: translate('email') },
  ];

  return (
    <Form actions={actions}>
      {errorMessage && (
        <Banner id="warning" variant="urgent" messages={[translate(errorMessage, MaxNumberOfCustomers)]} />
      )}
      <Card style={{ padding: 0 }} groupId="popover">
        <StyledTable hoverHighlight={true} padding={'default'} columns={columns} data={data} />
      </Card>
    </Form>
  );
};

export default BulkUploadPersona;
